import { Avatar, Popover } from 'antd';
import React, { useMemo, memo, useState } from 'react';
import { hashedColor } from '@helpers/hashedColor';
import EmailAvatar from '@assets/icons/email-avatar.svg';
import { ReactComponent as ApprovedWithFeedbackSvg } from '@assets/icons/AWStatuses/approved-with-feedback.svg';
import { ReactComponent as ApprovedSvg } from '@assets/icons/AWStatuses/approved.svg';
import { ReactComponent as RequireChangesSvg } from '@assets/icons/AWStatuses/require-changes.svg';
import { ReactComponent as EyeSvg } from '@assets/icons/open-eye-v2.svg';
import { ReactComponent as EyeGreenSvg } from '@assets/icons/open-eye-v2--green.svg';
import { ReactComponent as OverdueSvg } from '@assets/icons/AWStatuses/overdue.svg';
import { AvatarSize } from 'antd/lib/avatar/SizeContext';
import classNames from 'classnames';
import type { MemberDecisionProps } from '@components/Approval/MemberDecision';

import './UserAvatar.less';

const MemberDecision = React.lazy(
  () => import('@components/Approval/MemberDecision')
);

type UserAvatarProps = {
  userName: string;
  size: AvatarSize | undefined;
  src?: string | null;
  userEmail: string;
  isActive: boolean;
  className?: string;
  style?: React.CSSProperties;
  isShowDecision?: boolean;
  decision?: MemberDecisionProps['decision'];
};

function UserDecision({
  decision
}: {
  decision?: UserAvatarProps['decision'];
}) {
  const icon = useMemo(() => {
    if (!decision) return <EyeSvg />;
    if (decision === 'approved') return <ApprovedSvg />;
    if (decision === 'changes_required') return <RequireChangesSvg />;
    if (decision === 'approved_with_feedback')
      return <ApprovedWithFeedbackSvg />;
    if (decision === 'view') return <EyeGreenSvg />;
    if (decision === 'overdue') return <OverdueSvg />;
    return <EyeSvg />;
  }, [decision]);

  return <div className="custom_avatar--decision">{icon}</div>;
}

export default memo(function UserAvatar({
  userName,
  size,
  src,
  userEmail,
  isActive,
  className,
  style,
  showUserPopover = false,
  isShowDecision,
  canSubmitDecision,
  decision
}: UserAvatarProps & {
  showUserPopover?: boolean;
  canSubmitDecision?: boolean;
}) {
  const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false);
  return (
    <Popover
      open={isPopoverVisible}
      onOpenChange={(v) => {
        if (!showUserPopover) return;
        setIsPopoverVisible(v);
      }}
      trigger="hover"
      placement="bottom"
      overlayClassName="users-list-popover_overlay"
      content={
        <div className="users-list-popover">
          <div className="users-list-popover--item">
            <div className="user-information_wrapper">
              <AppAvatar
                isActive={isActive}
                size={42}
                src={src}
                userEmail={userEmail}
                userName={userName}
                decision={decision}
              />
              <div className="user-information">
                <div className="user-information--name">{userName}</div>
                <div className="user-information--email">{userEmail}</div>
              </div>
            </div>
            {isShowDecision && canSubmitDecision && (
              <div className="user-information--decision">
                <MemberDecision decision={decision} />
              </div>
            )}
          </div>
        </div>
      }
    >
      <AppAvatar
        isActive={isActive}
        size={size}
        src={src}
        userEmail={userEmail}
        userName={userName}
        className={className}
        style={style}
        isShowDecision={isShowDecision}
        decision={decision}
      />
      {/* need this {' '} to make popover works */}{' '}
    </Popover>
  );
});

function AppAvatar({
  userName,
  size,
  src,
  userEmail,
  isActive,
  className,
  style,
  isShowDecision,
  decision
}: UserAvatarProps) {
  const prepareName = useMemo(() => {
    const nameArr = userName ? userName.split(' ') : userEmail.split(' ');
    let name = `${nameArr[0]?.[0]?.toUpperCase() || ''}${
      nameArr[1]?.[0]?.toUpperCase() || ''
    }`;
    if (size && +size <= 24)
      name =
        nameArr[0]?.[0]?.toUpperCase() || nameArr[1]?.[0]?.toUpperCase() || '';
    return name;
  }, [userName, userEmail, size]);
  const avatarSrc = useMemo(() => {
    if (!isActive) return EmailAvatar;
    if (!src) return <>{prepareName}</>;
    return src;
  }, [prepareName, isActive, src]);

  return (
    <div className="custom_avatar-wrapper">
      <Avatar
        className={classNames('custom_avatar', className)}
        size={size}
        src={avatarSrc}
        style={{
          background: userEmail && hashedColor(userEmail, 'profileAvatar'),
          ...(style ?? {})
        }}
      />
      {isShowDecision && <UserDecision decision={decision} />}
    </div>
  );
}
